/**
 * SessionActionsBar - Top action bar with session controls
 * Includes: Copy link, QR toggle, Leaderboard toggle, Statistics toggle, Participants toggle
 */
import React from 'react';
import PropTypes from 'prop-types';

/**
 * Action Toggle Button
 */
const ActionButton = ({ 
  onClick, 
  active, 
  activeColor = 'primary', 
  icon, 
  label, 
  badge = null 
}) => {
  const colorClasses = {
    primary: 'bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300',
    secondary: 'bg-secondary-100 dark:bg-secondary-900/50 text-secondary-700 dark:text-secondary-300',
    success: 'bg-success-100 dark:bg-success-900/50 text-success-700 dark:text-success-300',
    warning: 'bg-warning-100 dark:bg-warning-900/50 text-warning-700 dark:text-warning-300',
    danger: 'bg-danger-100 dark:bg-danger-900/50 text-danger-700 dark:text-danger-300'
  };

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg theme-transition whitespace-nowrap ${
        active 
          ? colorClasses[activeColor]
          : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
      }`}
    >
      <span className="flex-shrink-0">{icon}</span>
      {label}
      {badge !== null && (
        <span className="bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded text-xs">
          {badge}
        </span>
      )}
    </button>
  );
};

ActionButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  active: PropTypes.bool,
  activeColor: PropTypes.oneOf(['primary', 'secondary', 'success', 'warning', 'danger']),
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  badge: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

/**
 * Copy Link Button with success feedback
 */
const CopyLinkButton = ({ onClick, copySuccess }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg theme-transition whitespace-nowrap"
  >
    {copySuccess ? (
      <svg className="w-4 h-4 flex-shrink-0 text-success-600 dark:text-success-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    )}
    Link kopieren
  </button>
);

CopyLinkButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  copySuccess: PropTypes.string
};

/**
 * Participants Panel (collapsible list of participants)
 */
const ParticipantsPanel = ({ participants }) => {
  if (Object.keys(participants).length === 0) return null;
  
  return (
    <div className="mt-3 bg-white dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
      <div className="flex flex-wrap gap-2">
        {Object.entries(participants).map(([id, data]) => (
          <span 
            key={id} 
            className="bg-gray-100 dark:bg-gray-600 px-3 py-1 rounded-full text-sm flex items-center gap-2 text-gray-700 dark:text-gray-200"
            title={`Beigetreten: ${new Date(data.joinedAt).toLocaleTimeString()}`}
          >
            {data.name}
            {data.totalScore !== undefined && (
              <span className="bg-primary-500 text-white px-2 py-0.5 rounded text-xs">
                {data.totalScore}
              </span>
            )}
          </span>
        ))}
      </div>
    </div>
  );
};

ParticipantsPanel.propTypes = {
  participants: PropTypes.object.isRequired
};

/**
 * Main SessionActionsBar Component
 */
function SessionActionsBar({
  // Session
  sessionCode,
  // Copy link
  onCopyLink,
  copySuccess,
  // Toggles
  showQRCode,
  onToggleQRCode,
  showLeaderboard,
  onToggleLeaderboard,
  showStatistics,
  onToggleStatistics,
  showParticipants,
  onToggleParticipants,
  // Actions
  onOpenSettings,
  onResetQuiz,
  // Data
  participantCount,
  participants
}) {
  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <CopyLinkButton onClick={onCopyLink} copySuccess={copySuccess} />
            
            <ActionButton
              onClick={onToggleQRCode}
              active={showQRCode}
              activeColor="secondary"
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                </svg>
              }
              label="QR-Code"
            />
            
            <ActionButton
              onClick={onToggleLeaderboard}
              active={showLeaderboard}
              activeColor="primary"
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              }
              label="Rangliste"
            />
            
            <ActionButton
              onClick={onToggleStatistics}
              active={showStatistics}
              activeColor="warning"
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              }
              label="Statistik"
            />
            
            <div className="w-px h-6 bg-gray-300 dark:bg-gray-600" />
            
            <button
              onClick={onOpenSettings}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg theme-transition whitespace-nowrap"
            >
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Einstellungen
            </button>
            
            <button
              onClick={onResetQuiz}
              className="flex items-center gap-2 px-3 py-2 text-sm text-warning-700 dark:text-warning-400 hover:text-warning-900 dark:hover:text-warning-300 hover:bg-warning-50 dark:hover:bg-warning-900/20 rounded-lg theme-transition whitespace-nowrap"
            >
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Neu starten
            </button>
            
            {sessionCode && (
              <>
                <div className="w-px h-6 bg-gray-300 dark:bg-gray-600" />
                
                <div className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-mono">
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                  </svg>
                  {sessionCode}
                </div>
              </>
            )}
          </div>
          
          <ActionButton
            onClick={onToggleParticipants}
            active={showParticipants}
            activeColor="success"
            icon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            }
            label="Teilnehmer"
            badge={participantCount}
          />
        </div>
        
        {/* Expandable Participants Panel */}
        {showParticipants && participantCount > 0 && (
          <ParticipantsPanel participants={participants} />
        )}
      </div>
    </div>
  );
}

SessionActionsBar.propTypes = {
  // Session
  sessionCode: PropTypes.string,
  // Copy link
  onCopyLink: PropTypes.func.isRequired,
  copySuccess: PropTypes.string,
  // Toggles
  showQRCode: PropTypes.bool.isRequired,
  onToggleQRCode: PropTypes.func.isRequired,
  showLeaderboard: PropTypes.bool.isRequired,
  onToggleLeaderboard: PropTypes.func.isRequired,
  showStatistics: PropTypes.bool.isRequired,
  onToggleStatistics: PropTypes.func.isRequired,
  showParticipants: PropTypes.bool.isRequired,
  onToggleParticipants: PropTypes.func.isRequired,
  // Actions
  onOpenSettings: PropTypes.func.isRequired,
  onResetQuiz: PropTypes.func.isRequired,
  // Data
  participantCount: PropTypes.number.isRequired,
  participants: PropTypes.object.isRequired
};

export { ActionButton, CopyLinkButton, ParticipantsPanel };
export default SessionActionsBar;
